import { useState, useEffect, useCallback } from 'react'
import { SchemaExplorer } from '@/components/admin/SchemaExplorer'
import { SqlEditor } from '@/components/admin/SqlEditor'
import { QueryResultsTable } from '@/components/admin/QueryResultsTable'
import logger from '@/utils/logger'
import {
  useAdminExecuteQueryGraphQL,
  type QueryResult,
} from '@/hooks/graphql/useAdminQueryGraphQL'

/**
 * Admin SQL Query Console
 *
 * Admin-only SQL query console with:
 * - CodeMirror SQL editor with syntax highlighting
 * - TanStack Table for query results
 * - Schema browser sidebar
 * - Read-only mode (SELECT queries only)
 *
 * Feature: 055-db-console
 */
const HISTORY_KEY = 'admin-query-history'
const MAX_HISTORY = 10

export function AdminQueryConsolePage() {
  const [query, setQuery] = useState('SELECT * FROM users LIMIT 10;')
  const [results, setResults] = useState<QueryResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [executionTime, setExecutionTime] = useState<number | null>(null)
  const { executeQuery, loading } = useAdminExecuteQueryGraphQL()
  const [queryHistory, setQueryHistory] = useState<string[]>(() => {
    try {
      const saved = localStorage.getItem(HISTORY_KEY)
      return saved ? JSON.parse(saved) : []
    } catch {
      return []
    }
  })

  const addToHistory = useCallback(
    (sql: string) => {
      // Remove duplicates and add to front, limit to MAX_HISTORY
      const updated = [sql, ...queryHistory.filter((q) => q !== sql)].slice(
        0,
        MAX_HISTORY
      )
      setQueryHistory(updated)
      try {
        localStorage.setItem(HISTORY_KEY, JSON.stringify(updated))
      } catch (err) {
        logger.error('AdminQueryConsole', 'Failed to save query history', {
          error: err,
        })
      }
    },
    [queryHistory]
  )

  const handleTableSelect = (tableName: string) => {
    // Validate tableName: only allow alphanumeric and underscores
    if (!/^[a-zA-Z0-9_]+$/.test(tableName)) {
      logger.error('AdminQueryConsole', 'Invalid table name selected', {
        tableName,
      })
      setError('Invalid table name selected.')
      return
    }

    const newQuery = `SELECT * FROM ${tableName} LIMIT 10;`
    setQuery(newQuery)
    logger.info('AdminQueryConsole', 'Table selected', { tableName })
  }

  const handleExecute = useCallback(async () => {
    setError(null)
    const startTime = Date.now()

    try {
      logger.info('AdminQueryConsole', 'Executing query', {
        query: query.substring(0, 100),
      })

      const data = await executeQuery(query)
      if (data) {
        setResults(data)
        setExecutionTime(Date.now() - startTime)

        // Add to history on success
        addToHistory(query)

        logger.info('AdminQueryConsole', 'Query executed successfully', {
          rowCount: data.rowCount,
          executionTime: Date.now() - startTime,
        })
      }
    } catch (err) {
      // Extract error message from GraphQL error
      let errorMessage: string
      if (
        err &&
        typeof err === 'object' &&
        'graphQLErrors' in err &&
        Array.isArray((err as any).graphQLErrors) &&
        (err as any).graphQLErrors.length > 0
      ) {
        // Apollo GraphQL error
        errorMessage = (err as any).graphQLErrors[0].message
      } else if (
        err &&
        typeof err === 'object' &&
        'message' in err &&
        typeof (err as any).message === 'string'
      ) {
        errorMessage = (err as any).message
      } else if (typeof err === 'string') {
        errorMessage = err
      } else {
        errorMessage = 'An unknown error occurred'
      }

      setError(errorMessage)
      setResults(null)
      logger.error('AdminQueryConsole', 'Query execution failed', { error: err })
    }
  }, [query, addToHistory, executeQuery])

  const handleClear = useCallback(() => {
    setQuery('')
    setResults(null)
    setError(null)
    setExecutionTime(null)
    logger.info('AdminQueryConsole', 'Editor cleared')
  }, [])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault()
        if (query.trim() && !loading) {
          handleExecute()
        }
      }

      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        handleClear()
      }

      if (e.key === 'Escape' && error) {
        setError(null)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [query, loading, error, handleExecute, handleClear])

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="border-b border-border bg-surface-1 px-6 py-4">
        <h1 className="text-2xl font-bold text-foreground">SQL Query Console</h1>
        <p className="text-sm text-muted">Execute SQL queries and browse database schema</p>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Sidebar - Schema Browser */}
        <div className="w-64">
          <SchemaExplorer onTableSelect={handleTableSelect} />
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col p-6 overflow-y-auto">
          <div className="space-y-6">
            {/* SQL Editor Section */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground">SQL Query</h2>

              <SqlEditor value={query} onChange={setQuery} />

              {/* Query Controls */}
              <div className="flex gap-2 items-center">
                <button
                  onClick={handleExecute}
                  disabled={loading || !query.trim()}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {loading ? 'Executing...' : 'Execute Query'}
                </button>

                <button
                  onClick={handleClear}
                  className="px-4 py-2 bg-surface-2 text-foreground rounded-md hover:bg-surface-3 transition-colors"
                >
                  Clear
                </button>

                {executionTime !== null && (
                  <span className="text-sm text-muted">
                    Executed in {executionTime}ms
                  </span>
                )}
              </div>

              {/* Query History */}
              {queryHistory.length > 0 && (
                <details className="group">
                  <summary className="cursor-pointer text-sm text-muted hover:text-foreground transition-colors">
                    Query History ({queryHistory.length})
                  </summary>
                  <div className="mt-2 space-y-1 max-h-48 overflow-auto border border-border rounded-md p-2 bg-surface-1">
                    {queryHistory.map((q, i) => (
                      <button
                        key={i}
                        onClick={() => setQuery(q)}
                        className="w-full text-left p-2 text-xs font-mono bg-surface-2 hover:bg-surface-3 rounded cursor-pointer transition-colors"
                      >
                        {q.substring(0, 100)}
                        {q.length > 100 ? '...' : ''}
                      </button>
                    ))}
                  </div>
                </details>
              )}

              {/* Keyboard Shortcuts Hint */}
              <div className="text-xs text-muted">
                <kbd className="px-1.5 py-0.5 bg-surface-2 border border-border rounded text-foreground">
                  ⌘/Ctrl + Enter
                </kbd>{' '}
                to execute •{' '}
                <kbd className="px-1.5 py-0.5 bg-surface-2 border border-border rounded text-foreground">
                  ⌘/Ctrl + K
                </kbd>{' '}
                to clear
              </div>
            </div>

            {/* Divider */}
            <hr className="border-border" />

            {/* Results Section */}
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-foreground">Results</h2>

              {/* Error Display */}
              {error && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-md text-red-600">
                  <strong>Error:</strong> {error}
                </div>
              )}

              {/* Empty State */}
              {!results && !error && (
                <div className="bg-surface-1 rounded-lg border border-border p-6">
                  <p className="text-muted">Execute a query to see results</p>
                </div>
              )}

              {/* Results Table */}
              {results && <QueryResultsTable data={results} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
